import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BackButton } from '../components/BackButton.js';
import {
  WidgetApiError,
  widgetApi,
  type Availability,
  type Slot,
  type VenueDetail,
} from '../lib/api.js';
import { isValidPhone, toE164 } from '../lib/phone.js';

/**
 * Times, grouped by shift.
 *
 * Unbookable slots are NOT hidden. A guest who sees an empty screen assumes the
 * widget is broken; one who sees the evening greyed out knows to try another
 * date - or to join the waitlist, which is offered right here rather than as a
 * separate journey they have to go and find.
 *
 * "Only a few tables left" comes from the seating engine's confidence field, so
 * it is a fact rather than a growth tactic: it means fewer than three bookable
 * units remain for this party size at that time.
 */
export function SlotStep({
  availability,
  loading,
  selected,
  venue,
  locale,
  partySize,
  serviceDate,
  onSelect,
  onBack,
}: {
  availability: Availability | null;
  loading: boolean;
  selected: Slot | null;
  venue: VenueDetail;
  locale: string;
  partySize: number;
  serviceDate: string;
  onSelect: (slot: Slot) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistPhone, setWaitlistPhone] = useState('');
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [waitlistError, setWaitlistError] = useState<string | null>(null);

  const byShift = useMemo(() => {
    const groups = new Map<string, Slot[]>();
    for (const slot of availability?.slots ?? []) {
      const list = groups.get(slot.shift_name) ?? [];
      list.push(slot);
      groups.set(slot.shift_name, list);
    }
    return [...groups.entries()];
  }, [availability]);

  const anyBookable = (availability?.slots ?? []).some((slot) => slot.bookable);
  const anyLimited = (availability?.slots ?? []).some(
    (slot) => slot.bookable && slot.confidence === 'limited',
  );

  async function joinWaitlist() {
    setWaitlistError(null);
    if (!isValidPhone(waitlistPhone)) {
      setWaitlistError(t('widget.phoneInvalid'));
      return;
    }
    try {
      await widgetApi.joinWaitlist({
        venue_slug: venue.slug,
        service_date: serviceDate,
        party_size: partySize,
        first_name: waitlistName,
        phone: toE164(waitlistPhone) ?? waitlistPhone,
        locale: locale === 'ar' ? 'ar' : 'en',
      });
      setWaitlistJoined(true);
    } catch (err) {
      setWaitlistError(
        err instanceof WidgetApiError && err.isRateLimited
          ? t('errors.rateLimited')
          : t('errors.generic'),
      );
    }
  }

  if (loading) {
    return <p className="py-8 text-center text-sm text-slate-500">{t('app.loading')}</p>;
  }

  if (availability?.closed) {
    return (
      <section className="rounded-lg border border-slate-200 p-6 text-center">
        <p className="text-sm text-slate-600">{t('widget.closed')}</p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-4">
      <BackButton onClick={onBack} />
      <h2 className="text-sm font-medium">{t('common.time')}</h2>

      {byShift.map(([shiftName, slots]) => (
        <div key={shiftName} className="flex flex-col gap-2">
          <h3 className="text-xs uppercase tracking-wide text-brand-bronze">{shiftName}</h3>
          <div className="grid grid-cols-4 gap-2">
            {slots.map((slot) => (
              <button
                key={slot.starts_at}
                type="button"
                data-testid={slot.bookable ? 'slot' : 'slot-unavailable'}
                disabled={!slot.bookable}
                onClick={() => onSelect(slot)}
                aria-pressed={selected?.starts_at === slot.starts_at}
                className={[
                  'rounded-lg border py-2 font-mono text-sm transition-colors',
                  selected?.starts_at === slot.starts_at
                    ? 'border-brand-teal bg-brand-teal text-white'
                    : slot.bookable
                      ? 'border-slate-200 hover:border-brand-teal'
                      : 'border-slate-100 text-slate-300',
                  slot.bookable && slot.confidence === 'limited'
                    ? 'ring-1 ring-brand-bronze'
                    : '',
                ].join(' ')}
                data-numeric
              >
                {slot.clock}
              </button>
            ))}
          </div>
        </div>
      ))}

      {anyLimited ? <p className="text-xs text-brand-bronze">{t('widget.fewLeft')}</p> : null}

      {/* Fully booked is a real answer, and the waitlist belongs here rather
          than behind another click. */}
      {!anyBookable && availability ? (
        <div className="flex flex-col gap-2 rounded-lg border border-brand-bronze bg-brand-bronze-light p-4">
          <p className="text-sm font-medium">{t('widget.fullyBooked')}</p>

          {waitlistJoined ? (
            <p className="text-sm text-brand-teal-dark">{t('widget.waitlistJoined')}</p>
          ) : (
            <>
              <input
                value={waitlistName}
                onChange={(event) => setWaitlistName(event.target.value)}
                placeholder={t('drawer.firstName')}
                autoComplete="given-name"
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
              />
              <input
                value={waitlistPhone}
                onChange={(event) => setWaitlistPhone(event.target.value)}
                placeholder={t('drawer.phone')}
                inputMode="tel"
                autoComplete="tel"
                className="rounded-md border border-slate-200 px-3 py-2 font-mono text-sm"
              />
              {waitlistError ? (
                <p className="text-xs text-red-700" role="alert">
                  {waitlistError}
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => void joinWaitlist()}
                disabled={!waitlistName || !waitlistPhone}
                className="rounded-lg bg-brand-bronze py-2.5 text-sm font-medium text-white hover:bg-brand-bronze-dark disabled:opacity-40"
              >
                {t('widget.joinWaitlist')}
              </button>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
